/**
 * Escalation Types — P-W34 Phase 3 (Practice-Web)
 *
 * Cross-Profession Eskalation Care → Praxis.
 * Tabelle: care_practice_escalations (Phase 1 RPCs siehe Mobile-Service).
 *
 * Pattern gespiegelt aus types/care-practice-request.ts (Sibling-Architektur):
 * - String-Union-Types statt enums (idiomatic Tailwind+TS)
 * - STATUS_COLORS Record für inline-pill rendering
 * - STATUS_LABELS für DE-Anzeige
 */

export type EscalationStatus =
    | 'draft'
    | 'sent'
    | 'delivered'
    | 'read'
    | 'acknowledged'
    | 'in_action'
    | 'resolved'
    | 'closed'
    | 'cancelled'
    | 'timeout_escalated';

export type EscalationSeverity = 'routine' | 'urgent' | 'critical';

export type EscalationCategory =
    | 'vital_signs'
    | 'medication'
    | 'wound_change'
    | 'fall_event'
    | 'behavior_change'
    | 'symptom_new'
    | 'rezept_anforderung'
    | 'visite_anforderung';

export type EscalationActionType =
    | 'callback'
    | 'prescription'
    | 'home_visit'
    | 'instructions_only';

export type EscalationActorType = 'care_staff' | 'practice_staff' | 'system_timeout';

export interface Escalation {
    id: string;
    facilityId: string;
    practiceId: string;
    residentId: string;
    partnershipId: string;
    originatingStaffId: string;
    originatingShiftType: string | null;
    originatingShiftDate: string | null;
    severity: EscalationSeverity;
    category: EscalationCategory;
    subject: string;
    body: string;
    state: EscalationStatus;
    sentAt: string | null;
    deliveredAt: string | null;
    firstReadAt: string | null;
    acknowledgedAt: string | null;
    acknowledgedByPracticeStaffId: string | null;
    actionSetAt: string | null;
    actionType: EscalationActionType | null;
    actionNote: string | null;
    resolvedAt: string | null;
    resolvedByPracticeStaffId: string | null;
    resolutionNote: string | null;
    closedAt: string | null;
    closureConfirmationNote: string | null;
    cancelledAt: string | null;
    cancellationReason: string | null;
    slaDeliveredBy: string | null;
    slaReadBy: string | null;
    slaAcknowledgedBy: string | null;
    createdAt: string;
    updatedAt: string;
    // Joined columns for inbox display (optional)
    residentFirstName?: string | null;
    residentLastName?: string | null;
    facilityName?: string | null;
}

export interface EscalationAuditEntry {
    id: string;
    escalationId: string;
    fromState: EscalationStatus | null;
    toState: EscalationStatus;
    actorType: EscalationActorType;
    transitionedAt: string;
    metadata: Record<string, unknown> | null;
}

export interface EscalationStats {
    total: number;
    awaiting: number; // sent + delivered + read (vom Arzt noch nicht bestätigt)
    inProgress: number; // acknowledged + in_action
    resolved: number;
}

// ──────────────────────────────────────────────────────────────────────────
// Display labels + colors (German hardcoded, mirror care-practice-request.ts)
// ──────────────────────────────────────────────────────────────────────────

export const STATUS_LABELS: Record<EscalationStatus, string> = {
    draft: 'Entwurf',
    sent: 'Neu eingegangen',
    delivered: 'Zugestellt',
    read: 'Gelesen',
    acknowledged: 'Bestätigt',
    in_action: 'In Bearbeitung',
    resolved: 'Erledigt',
    closed: 'Abgeschlossen',
    cancelled: 'Storniert',
    timeout_escalated: 'SLA-Timeout',
};

export const STATUS_COLORS: Record<
    EscalationStatus,
    { text: string; bg: string; dot: string }
> = {
    draft: { text: 'text-slate-700', bg: 'bg-slate-100', dot: 'bg-slate-500' },
    sent: { text: 'text-amber-700', bg: 'bg-amber-100', dot: 'bg-amber-500' },
    delivered: { text: 'text-amber-700', bg: 'bg-amber-100', dot: 'bg-amber-500' },
    read: { text: 'text-blue-700', bg: 'bg-blue-100', dot: 'bg-blue-500' },
    acknowledged: { text: 'text-teal-700', bg: 'bg-teal-100', dot: 'bg-teal-500' },
    in_action: { text: 'text-teal-700', bg: 'bg-teal-100', dot: 'bg-teal-500' },
    resolved: { text: 'text-emerald-700', bg: 'bg-emerald-100', dot: 'bg-emerald-500' },
    closed: { text: 'text-slate-600', bg: 'bg-slate-100', dot: 'bg-slate-400' },
    cancelled: { text: 'text-slate-500', bg: 'bg-slate-100', dot: 'bg-slate-400' },
    timeout_escalated: { text: 'text-red-700', bg: 'bg-red-100', dot: 'bg-red-500' },
};

export const SEVERITY_LABELS: Record<EscalationSeverity, string> = {
    routine: 'Routine',
    urgent: 'Dringend',
    critical: 'Kritisch',
};

export const SEVERITY_COLORS: Record<
    EscalationSeverity,
    { text: string; bg: string; dot: string }
> = {
    routine: { text: 'text-emerald-700', bg: 'bg-emerald-50', dot: 'bg-emerald-500' },
    urgent: { text: 'text-amber-700', bg: 'bg-amber-50', dot: 'bg-amber-500' },
    critical: { text: 'text-red-700', bg: 'bg-red-50', dot: 'bg-red-500' },
};

export const CATEGORY_LABELS: Record<EscalationCategory, string> = {
    vital_signs: 'Vitalzeichen',
    medication: 'Medikation',
    wound_change: 'Wunde',
    fall_event: 'Sturz',
    behavior_change: 'Verhalten',
    symptom_new: 'Neues Symptom',
    rezept_anforderung: 'Rezept-Anforderung',
    visite_anforderung: 'Visite-Anforderung',
};

export const ACTION_TYPE_LABELS: Record<EscalationActionType, string> = {
    callback: 'Rückruf',
    prescription: 'Rezept ausgestellt',
    home_visit: 'Hausbesuch geplant',
    instructions_only: 'Anweisungen schriftlich',
};

export const ACTION_TYPE_ICONS: Record<EscalationActionType, string> = {
    callback: '📞',
    prescription: '📄',
    home_visit: '🏠',
    instructions_only: '📝',
};

// State buckets for tab-filter logic
export const AWAITING_STATES: ReadonlyArray<EscalationStatus> = [
    'sent',
    'delivered',
    'read',
];

export const IN_PROGRESS_STATES: ReadonlyArray<EscalationStatus> = [
    'acknowledged',
    'in_action',
];

export const TERMINAL_STATES: ReadonlyArray<EscalationStatus> = [
    'resolved',
    'closed',
    'cancelled',
    'timeout_escalated',
];
